import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {
  getPricingSchema,
  getBreadcrumbSchema,
  getOrganizationSchema
} from '../utils/seoSchemas';

const features = [
  { name: 'AI-powered email booking intake', included: true },
  { name: 'Visual job board with drag-and-drop', included: true },
  { name: 'Digital vehicle health checks (eVHC)', included: true },
  { name: 'Photo and video uploads', included: true },
  { name: 'Customer payment links (Stripe)', included: true },
  { name: 'Automatic VAT-compliant invoicing', included: true },
  { name: 'SMS and email notifications', included: true },
  { name: 'Mobile-friendly interface', included: true },
  { name: 'Parts ordering integration (GSF, Euro Car Parts)', included: true },
  { name: 'MOT reminder system', included: true },
  { name: 'Multi-location support', included: true },
  { name: 'Public booking page', included: true },
  { name: 'Dedicated onboarding support', included: true },
];

const Pricing = () => {
  const pricingSchema = getPricingSchema();
  const organizationSchema = getOrganizationSchema();
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Pricing', url: 'https://www.go-ops.digital/pricing' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Pricing | Go Ops - AI Garage Management Software</title>
        <meta
          name="description"
          content="Go Ops garage management software pricing. Contact us for a custom quote based on your garage size and needs."
        />
        <link rel="canonical" href="https://www.go-ops.digital/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Pricing | Go Ops - AI Garage Management Software" />
        <meta
          property="og:description"
          content="Go Ops garage management software pricing. Contact us for a custom quote based on your garage size and needs."
        />
        <meta property="og:url" content="https://www.go-ops.digital/pricing" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing | Go Ops - AI Garage Management Software" />
        <meta
          name="twitter:description"
          content="Go Ops garage management software pricing. Contact us for a custom quote based on your garage size and needs."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(pricingSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/faq" sx={{ color: 'white' }}>
                FAQ
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Go Ops Pricing
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Flexible pricing tailored to your garage size and needs
          </Typography>
        </Container>
      </Box>

      {/* Pricing Card */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Card sx={{ maxWidth: 500, mx: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
              Custom Pricing
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Get a quote tailored to your garage
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#ff6b35', mb: 1 }}>
              Contact Us
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              for a personalised quote
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' }, mb: 2 }}
            >
              Request a Demo
            </Button>
            <Typography variant="body2" color="text.secondary">
              30-minute demo included
            </Typography>
          </CardContent>
        </Card>

        {/* Features Table */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}>
            Everything You Need
          </Typography>
          <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#2c3e50' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Feature</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Included</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' } }}>
                    <TableCell>{feature.name}</TableCell>
                    <TableCell align="center">
                      {feature.included && <CheckIcon sx={{ color: '#28a745' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Ready to streamline your garage?
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            Book a demo to see Go Ops in action and get your custom quote.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              size="large"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component="a"
              href="tel:+443332101500"
              size="large"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              Call +44 333 2101 500
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/about" sx={{ color: 'white', textTransform: 'none' }}>
                About
              </Button>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/faq" sx={{ color: 'white', textTransform: 'none' }}>
                FAQ
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Pricing;
